// Tutorial — the family's logical journey with remembr, end-to-end.
// Built for two audiences at once: families & carers (warm), and
// distributors / care-home managers (credible). Five chapters track the
// flow from the box arriving on the doorstep through to John recording
// an early message back to his family while the long-term memories are
// still flowing.
//
// Copy lives in i18next under the `tutorial.*` namespace (translated to all
// 12 locales). Chapter visuals (images + icons) stay in this file.
//
// Mirrored as a downloadable PDF via /api/admin/export/tutorial.pdf
// (build_tutorial_pdf in /app/backend/pdf_export.py).
import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import {
    PackageIcon,
    PowerIcon,
    UploadSimpleIcon,
    GlobeHemisphereWestIcon,
    MicrophoneIcon,
    HeartIcon,
    HeadphonesIcon,
    PlayCircleIcon,
    ImageSquareIcon,
    UsersThreeIcon,
    PaperPlaneTiltIcon,
    Clock,
    HandHeartIcon,
    SparkleIcon,
    ArrowRight,
    CheckCircleIcon,
} from "@phosphor-icons/react";
import { SiteNav } from "../components/SiteNav";
import { Footer } from "../components/sections/Footer";
import { FmnWatermark } from "../components/FmnWatermark";
import { ForgetMeNotIcon } from "../components/ForgetMeNotIcon";

const FADE = {
    initial: { opacity: 0, y: 22 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-80px" },
    transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1] },
};

// Brand palette mirrors design_guidelines + pdf_export.
const LAVENDER = "#6B7DB8";
const LAVENDER_DEEP = "#4A5A95";
const GOLD = "#F4D068";

// Static visual config — text comes from i18n at render time.
const CHAPTERS = [
    {
        num: "01",
        image: "/brand/packaging/adult-box-open-unboxing.png",
        accent: LAVENDER,
        icons: [PackageIcon, HeadphonesIcon, ForgetMeNotIcon],
    },
    {
        num: "02",
        image: "/brand/product/adult-lavender-bedside.png",
        accent: LAVENDER_DEEP,
        icons: [PowerIcon, HeadphonesIcon, PlayCircleIcon],
    },
    {
        num: "03",
        image: "/family-at-table.png",
        accent: GOLD,
        icons: [UsersThreeIcon, MicrophoneIcon, ImageSquareIcon],
        hasSteps: true,
        stepIcons: [ImageSquareIcon, MicrophoneIcon, UploadSimpleIcon],
    },
    {
        num: "04",
        image: "/john-films-tv.png",
        accent: LAVENDER,
        icons: [GlobeHemisphereWestIcon, PaperPlaneTiltIcon, UsersThreeIcon],
    },
    {
        num: "05",
        image: "/brand/product/adult-lavender-inhand.png",
        accent: LAVENDER_DEEP,
        icons: [MicrophoneIcon, HeartIcon, Clock],
    },
];

const QUICK_STEP_ICONS = [PackageIcon, PowerIcon, UploadSimpleIcon, GlobeHemisphereWestIcon, MicrophoneIcon];
const QUICK_STEP_KEYS = ["box", "start", "playlist", "remote", "records_back"];

export default function Tutorial() {
    const { t } = useTranslation();
    useEffect(() => {
        window.scrollTo({ top: 0, behavior: "instant" });
    }, []);

    return (
        <div className="min-h-screen bg-[#FDFBF7] text-[#2D2B2A]" data-testid="tutorial-page">
            <SiteNav />

            {/* HERO */}
            <section
                className="relative pt-64 pb-20 px-6 overflow-hidden rem-petal-bg"
                data-testid="tutorial-hero"
            >
                <FmnWatermark position="right" size={720} opacity={0.06} rotate={-12} />
                <FmnWatermark position="bottom-left" size={560} opacity={0.05} rotate={18} />
                <div className="relative max-w-5xl mx-auto">
                    <motion.div {...FADE} className="flex items-center gap-3 mb-6">
                        <ForgetMeNotIcon size={42} />
                        <span className="font-sans text-xs uppercase tracking-[0.3em] text-[#6B7DB8] font-semibold">
                            {t("tutorial.eyebrow")}
                        </span>
                    </motion.div>
                    <motion.h1
                        {...FADE}
                        transition={{ ...FADE.transition, delay: 0.1 }}
                        className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-tight tracking-tight"
                        data-testid="tutorial-headline"
                    >
                        {t("tutorial.headline_part1")}{" "}
                        <em className="italic text-[#4A5A95]">{t("tutorial.headline_emphasis")}</em>
                    </motion.h1>
                    <motion.p
                        {...FADE}
                        transition={{ ...FADE.transition, delay: 0.2 }}
                        className="mt-8 text-lg sm:text-xl text-[#5C5A59] leading-relaxed max-w-3xl font-light"
                    >
                        {t("tutorial.intro")}
                    </motion.p>

                    {/* Quick journey strip */}
                    <motion.div
                        {...FADE}
                        transition={{ ...FADE.transition, delay: 0.3 }}
                        className="mt-14 bg-white/70 backdrop-blur rounded-3xl p-6 sm:p-8 border border-[#E5E1D8]/60 shadow-rem-soft"
                        data-testid="tutorial-quickstrip"
                    >
                        <p className="font-sans text-[10px] uppercase tracking-[0.3em] text-[#6B7DB8] font-semibold mb-5">
                            {t("tutorial.flow_label")}
                        </p>
                        <ol className="grid grid-cols-2 md:grid-cols-5 gap-4">
                            {QUICK_STEP_KEYS.map((key, i) => {
                                const Icon = QUICK_STEP_ICONS[i];
                                return (
                                    <li
                                        key={key}
                                        className="flex flex-col items-start gap-3"
                                        data-testid={`tutorial-quickstep-${i + 1}`}
                                    >
                                        <div className="flex items-center gap-2">
                                            <span className="font-serif text-2xl text-[#8C9ECB]">
                                                {String(i + 1).padStart(2, "0")}
                                            </span>
                                            <Icon size={20} weight="light" color={LAVENDER_DEEP} />
                                        </div>
                                        <p className="text-sm text-[#2D2B2A] leading-snug">
                                            {t(`tutorial.flow.${key}`)}
                                        </p>
                                    </li>
                                );
                            })}
                        </ol>
                    </motion.div>

                    {/* Audience pills */}
                    <motion.div
                        {...FADE}
                        transition={{ ...FADE.transition, delay: 0.4 }}
                        className="mt-10 flex flex-wrap items-center gap-3 text-sm"
                        data-testid="tutorial-audience-pills"
                    >
                        <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#F4F6FB] text-[#4A5A95] border border-[#8C9ECB]/40">
                            <HandHeartIcon size={14} weight="fill" /> {t("tutorial.audience_families")}
                        </span>
                        <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white text-[#8A6A0E] border border-[#F4D068]/60">
                            <SparkleIcon size={14} weight="fill" /> {t("tutorial.audience_clinicians")}
                        </span>
                        <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white text-[#5C5A59] border border-[#E5E1D8]">
                            <Clock size={14} weight="bold" /> {t("tutorial.audience_time")}
                        </span>
                    </motion.div>
                </div>
            </section>

            {/* CHAPTERS */}
            <div className="px-6">
                <div className="max-w-6xl mx-auto">
                    {CHAPTERS.map((ch, i) => (
                        <Chapter key={ch.num} chapter={ch} reverse={i % 2 === 1} />
                    ))}
                </div>
            </div>

            {/* CLOSING — the two-direction promise + CTA */}
            <section
                className="relative mt-10 py-28 px-6 bg-gradient-to-b from-white to-[#F4F6FB] overflow-hidden"
                data-testid="tutorial-close"
            >
                <FmnWatermark position="bottom-right" size={720} opacity={0.06} rotate={-12} />
                <div className="relative max-w-4xl mx-auto text-center">
                    <motion.div {...FADE}>
                        <ForgetMeNotIcon size={56} className="mx-auto mb-6" />
                        <p className="font-sans text-xs uppercase tracking-[0.3em] text-[#6B7DB8] font-semibold mb-5">
                            {t("tutorial.close_eyebrow")}
                        </p>
                        <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-tight">
                            {t("tutorial.close_headline_part1")}{" "}
                            <em className="italic text-[#4A5A95]">{t("tutorial.close_headline_emphasis")}</em>
                        </h2>
                        <p className="mt-8 text-lg text-[#5C5A59] leading-relaxed font-light">
                            {t("tutorial.close_body")}
                        </p>
                        <div className="mt-12 flex flex-wrap justify-center gap-4">
                            <Link
                                to="/demo/john-tilly"
                                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-[#6B7DB8] hover:bg-[#4A5A95] text-white text-base font-medium transition-all hover:-translate-y-0.5 shadow-rem-soft"
                                data-testid="tutorial-cta-john"
                            >
                                {t("tutorial.close_cta_john")} <ArrowRight size={16} weight="bold" />
                            </Link>
                            <Link
                                to="/sign-up"
                                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-white border border-[#8C9ECB] hover:border-[#6B7DB8] text-[#4A5A95] text-base font-medium transition-all hover:-translate-y-0.5"
                                data-testid="tutorial-cta-signup"
                            >
                                {t("tutorial.close_cta_signup")}
                            </Link>
                        </div>
                        <p className="mt-8 text-xs text-[#8A8786]">
                            {t("tutorial.close_footnote_part1")}{" "}
                            <a href="mailto:hello@remembr.it.com" className="underline hover:text-[#4A5A95]">hello@remembr.it.com</a>.
                        </p>
                    </motion.div>
                </div>
            </section>

            <Footer />
        </div>
    );
}

function Chapter({ chapter, reverse }) {
    const { t } = useTranslation();
    const { num, image, accent, icons, hasSteps, stepIcons } = chapter;
    const base = `tutorial.chapters.${num}`;
    const title = t(`${base}.title`);
    const kicker = t(`${base}.kicker`);
    const emphasis = t(`${base}.emphasis`);
    const body = t(`${base}.body`);
    const callout_label = t(`${base}.callout_label`);
    const callout_body = t(`${base}.callout_body`);
    const forCarer = t(`${base}.for_carer`, { returnObjects: true });
    const carerList = Array.isArray(forCarer) ? forCarer : [];
    const steps = hasSteps ? t(`${base}.steps`, { returnObjects: true }) : null;
    const stepList = Array.isArray(steps) ? steps : [];

    return (
        <section
            className="relative py-20 lg:py-28"
            data-testid={`tutorial-chapter-${num}`}
        >
            {/* Chapter ribbon — numeric marker + vertical thread */}
            <div className="absolute left-0 top-0 bottom-0 hidden lg:flex flex-col items-center w-16 select-none">
                <div className="w-px flex-1 bg-gradient-to-b from-transparent via-[#E5E1D8] to-transparent" />
                <p
                    className="font-serif italic text-7xl leading-none"
                    style={{ color: accent, opacity: 0.18 }}
                >
                    {num}
                </p>
                <div className="w-px flex-1 bg-gradient-to-b from-transparent via-[#E5E1D8] to-transparent" />
            </div>

            <div className={`grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start ${reverse ? "lg:[&>*:first-child]:order-2" : ""}`}>
                {/* Text column */}
                <motion.div
                    {...FADE}
                    transition={{ ...FADE.transition, delay: 0.1 }}
                    className="lg:col-span-6"
                >
                    <div className="flex items-center gap-3 mb-5">
                        <span
                            className="inline-flex items-center justify-center w-12 h-12 rounded-2xl text-white font-serif italic text-xl"
                            style={{ background: accent }}
                        >
                            {num}
                        </span>
                        <p className="font-sans text-xs uppercase tracking-[0.3em] font-semibold" style={{ color: accent }}>
                            {kicker}
                        </p>
                    </div>
                    <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-tight tracking-tight" data-testid={`tutorial-chapter-${num}-title`}>
                        {title}
                        <br />
                        <em className="italic text-[#4A5A95]">— {emphasis}.</em>
                    </h2>
                    <p className="mt-7 text-base sm:text-lg text-[#5C5A59] leading-relaxed font-light" data-testid={`tutorial-chapter-${num}-body`}>
                        {body}
                    </p>

                    {/* Steps strip (chapter 3 only) */}
                    {stepList.length > 0 && (
                        <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid={`tutorial-chapter-${num}-steps`}>
                            {stepList.map((label, i) => {
                                const Icon = stepIcons?.[i] || SparkleIcon;
                                return (
                                    <div
                                        key={i}
                                        className="rounded-2xl bg-white border border-[#E5E1D8]/70 p-5 shadow-rem-soft hover:-translate-y-0.5 transition-transform"
                                    >
                                        <div className="flex items-center justify-between mb-3">
                                            <Icon size={22} weight="light" color={LAVENDER_DEEP} />
                                            <span className="text-xs font-sans uppercase tracking-[0.2em] text-[#8A8786]">
                                                {String(i + 1).padStart(2, "0")}
                                            </span>
                                        </div>
                                        <p className="text-sm text-[#2D2B2A] leading-snug">{label}</p>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {/* For the carer card */}
                    <div
                        className="mt-10 rounded-3xl bg-[#F4F6FB] border border-[#8C9ECB]/30 p-7"
                        data-testid={`tutorial-chapter-${num}-carer`}
                    >
                        <p className="font-sans text-[10px] uppercase tracking-[0.3em] text-[#4A5A95] font-semibold mb-4">
                            {t("tutorial.for_carer_label")}
                        </p>
                        <ul className="space-y-3">
                            {carerList.map((line) => (
                                <li key={line} className="flex gap-3 items-start text-sm text-[#2D2B2A] leading-relaxed">
                                    <CheckCircleIcon size={18} weight="fill" color={LAVENDER} className="mt-0.5 shrink-0" />
                                    <span>{line}</span>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Credibility callout */}
                    <div
                        className="mt-6 rounded-2xl border-l-4 pl-5 pr-3 py-4"
                        style={{ borderColor: accent, background: "rgba(244, 246, 251, 0.5)" }}
                        data-testid={`tutorial-chapter-${num}-callout`}
                    >
                        <p className="font-sans text-[10px] uppercase tracking-[0.3em] font-semibold mb-1.5" style={{ color: accent }}>
                            {callout_label}
                        </p>
                        <p className="text-sm text-[#2D2B2A] leading-relaxed italic font-serif">{callout_body}</p>
                    </div>
                </motion.div>

                {/* Visual column */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 1.1, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
                    className="lg:col-span-6"
                >
                    <div className="relative">
                        <div className="absolute -top-4 -left-4 z-10 flex gap-2 sm:gap-3 bg-white rounded-2xl px-4 py-3 border border-[#E5E1D8] shadow-rem-soft">
                            {icons.map((I, i) => (
                                <I key={i} size={22} weight="light" color={accent} />
                            ))}
                        </div>
                        <div
                            className="aspect-[4/3] rounded-3xl overflow-hidden shadow-rem-card bg-white border border-[#E5E1D8]"
                            style={{ boxShadow: `0 1px 0 ${accent}33 inset` }}
                        >
                            <img
                                src={image}
                                alt={title}
                                className="w-full h-full object-cover"
                                loading="lazy"
                            />
                        </div>
                        <p className="mt-5 font-serif italic text-[#5C5A59] text-lg leading-snug">
                            {`${kicker} · ${title}`}
                        </p>
                    </div>
                </motion.div>
            </div>
        </section>
    );
}
